import useTravelUser from '@flight-workspace/common-client/src/hooks/useTravelUser';
import {
  useKoreanUnitSeparators,
  withPriceSeperator,
} from '@flight-workspace/common-client/src/util/number';
import classNames from 'classnames/bind';
import { IPromotion } from 'common/promotion';
import {
  getClickCodeWithPrefix,
  getNpayLabelInfo,
  hasTravelClubFare,
} from 'common/utils';
import config from 'config/index';
import { useReloadTimerContext } from 'contexts/reloadTimer';
import useFlightsCards from 'hooks/useFlightsCards';
import PromotionInfo from 'list/common/promotionInfo';
import getPromotions from '../promotion';
import { IFareDetail, IScheduleDetail } from '../types';
import { getPriceOfPerson, hasAdultNaverFare } from '../utils';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

interface IProps {
  fares: IFareDetail[];
  fareType: string;
  fareTypeName: string;
  isTopPopularFlight: boolean;
  nClickKey: string;
  priceTitle: string;
  promotions: IPromotion[];
  scheduleNumber: number;
  schedules: IScheduleDetail[][];
  showNpayLabelPopup?: string;
  selectFlight: (fareType: string) => void;
  setShowNpayLabelPopup: (text: string, duration?: number | undefined) => void;
}

export function Price({
  fares,
  fareType,
  fareTypeName,
  isTopPopularFlight,
  nClickKey,
  priceTitle,
  promotions,
  scheduleNumber,
  schedules,
  showNpayLabelPopup,
  selectFlight,
  setShowNpayLabelPopup,
}: IProps) {
  const flightCards = useFlightsCards();
  const { withReloadTimer } = useReloadTimerContext();
  const { isLogin, login } = useTravelUser({
    nAuthWithUrlParam: config.nAuthWithUrlParam,
  });
  const [minFare] = fares;
  const [promotion] = getPromotions(promotions, schedules, minFare);
  const isDiscountPromotion = hasAdultNaverFare(minFare);
  const isNPayPromotion = isDiscountPromotion && !!promotion;
  const isTravelClub = hasTravelClubFare(fares);
  const nPayLabelInfo = getNpayLabelInfo(fareType, flightCards);
  const withPrefix = getClickCodeWithPrefix(nClickKey);

  // 가격 리스트 레이어
  // 기본
  // - 공통: 트래블클럽 로고 노출
  // - 비로그인: 로그인 버튼 노출
  // - 로그인: 최저가 노출
  const defaultFareListItem = (
    isTravelClub: boolean,
    isPromotionByFare: boolean,
    fare: IFareDetail,
  ) => (
    <>
      <b className={cx('usual')} itemProp="offers">
        {priceTitle}
        <i className={cx('num')}>
          {withPriceSeperator(getPriceOfPerson(fare.Adult))}
        </i>
        <span className={cx('unit')}>원~</span>
      </b>
      {isTravelClub && !isLogin && !isPromotionByFare && (
        <a
          className={cx('login')}
          href="#"
          itemProp="offers"
          onClick={() => login(window.location.href)}
        >
          <span className={cx('txt')}>특가확인</span>
          <i className={cx('nlogin')} title="NAVER 로그인" />
        </a>
      )}
    </>
  );

  // 즉시할인 프로모션
  // 트래블클럽(할인요금) 존재시
  // 로그인/비로그인 UI 동일
  const directPromotionListItem = (fare: IFareDetail) => {
    const originalFare = getPriceOfPerson(fare.Adult);
    const discountFare = getPriceOfPerson(fare.Adult, true);
    return (
      <>
        <del className={cx('before')} itemProp="offers">
          <b className={cx('num')}>{withPriceSeperator(originalFare)}</b>
          <span className={cx('unit')}>원~</span>
        </del>
        <div className={cx('condition')} itemProp="itemCondition">
          <span className={cx('directpromotion')}>
            {useKoreanUnitSeparators(originalFare - discountFare)}원 할인
          </span>
        </div>
        <b className={cx('promoted')} itemProp="offers">
          {priceTitle}
          <b className={cx('num')}>{withPriceSeperator(discountFare)}</b>
          <span className={cx('unit')}>원~</span>
        </b>
      </>
    );
  };

  return (
    <div
      className={cx('item')}
      itemScope
      data-withmembership={!!isTravelClub && !!isLogin}
    >
      <button
        className={cx('anchor')}
        type="button"
        role="link"
        data-event-nlog="click"
        data-event-area-code={withPrefix(
          `${isTopPopularFlight ? 'top' : ''}faresel${scheduleNumber}`,
        )}
        onClick={withReloadTimer(() => {
          selectFlight(fareType);
        })}
      />
      <span className={cx('type')} itemProp="name">
        <span className={cx('name')}>{fareTypeName}</span>
      </span>
      {isTravelClub && (
        <div className={cx('condition')} itemProp="itemCondition">
          <span className={cx('travelclub')}>
            <i className={cx('service')} title="트래블클럽" />
            <span className={cx('txt')}>할인</span>
          </span>
        </div>
      )}
      {isDiscountPromotion
        ? directPromotionListItem(minFare)
        : defaultFareListItem(isTravelClub, isNPayPromotion, minFare)}
      {isNPayPromotion && (
        <div className={cx('condition')} itemProp="itemCondition" role="row">
          <div className={cx('promotion')}>
            <span className={cx('desc')}>
              <PromotionInfo
                npayIcon={<i className={cx('npay')} />}
                paymentMethod={
                  promotion.paymentMethod && fareTypeName.split('/')[1]
                }
                summaryByType={promotion.summaryByType}
              />
            </span>
            <span className={cx('price')}>
              <b className={cx('num')}>
                {withPriceSeperator(promotion.discountedPrice)}
              </b>
              <span className={cx('unit')}>원~</span>
            </span>
          </div>
        </div>
      )}
      {!!nPayLabelInfo && (
        <div className={cx('condition')} itemProp="itemCondition">
          <button
            className={cx('label_npay')}
            onClick={() =>
              setShowNpayLabelPopup(showNpayLabelPopup ? '' : 'show')
            }
            onMouseEnter={() =>
              setShowNpayLabelPopup(showNpayLabelPopup ? '' : 'show')
            }
            aria-haspopup="true"
            aria-expanded={!!showNpayLabelPopup}
          >
            <i className={cx('label')} aria-label="네이버페이" />
            {`${nPayLabelInfo.nPayPromotionAmount}% 적립`}
          </button>
          <div className={cx('popup')}>{nPayLabelInfo.nPayLabelPopupText}</div>
        </div>
      )}
    </div>
  );
}
